import {createSelector} from 'reselect';

import appConfig from '@/config/appConfig.typed';
import * as iccConsumerAggregatedAttributeTypes from '@/consts/icc/iccConsumerAggregatedAttributeTypes';
import {
    isJourneyActionCompleted,
    isJourneyCompleted,
    isJourneyInProgress,
    isJourneyLevelCompleted,
    isJourneyLevelInProgress,
} from '@/services/loyalty/loyaltyUtils';

import {makeSelectConsumerAggregatedAttribute, selectConsumer} from './consumer';

const selectIsLoyaltyMember = createSelector(
    makeSelectConsumerAggregatedAttribute(iccConsumerAggregatedAttributeTypes.IS_LOYALTY_MEMBER),
    (value) => {
        return value?.toLowerCase() === 'true';
    }
);

const selectLoyaltyLevel = createSelector(
    makeSelectConsumerAggregatedAttribute(iccConsumerAggregatedAttributeTypes.LOYALTY_LEVEL),
    (value) => {
        const loyaltyLevels = appConfig.getLoyaltyLevels();

        if (!loyaltyLevels?.length || !value) return;

        const level = loyaltyLevels.find((x) => x.loyaltyKey?.toLowerCase() === value.toLowerCase());

        if (!level) return;

        const nextLevel = loyaltyLevels.find((x) => x.order === level.order + 1);

        return {...level, nextLevel};
    }
);

const selectLoyaltyPoints = createSelector(
    makeSelectConsumerAggregatedAttribute(iccConsumerAggregatedAttributeTypes.LOYALTY_POINTS),
    (value) => {
        return parseInt(value) || 0;
    }
);

const selectLoyaltyJourneys = createSelector(selectConsumer, (state) => state.loyaltyJourneys);

const selectAllJourneys = createSelector(selectLoyaltyJourneys, (loyalty) => {
    const journeysArray = loyalty?.data?.memberJourneys || [];
    const availableJourneys = loyalty?.data?.journeys || [];

    return [...journeysArray, ...availableJourneys];
});

const selectAllExpiringJourneys = createSelector(selectAllJourneys, (journeys) => {
    return journeys.filter((journey) => {
        if (isJourneyCompleted(journey)) {
            return false;
        }

        const expireAt = journey?.journeyExpireAt || journey?.memberJourneyExpireAt;

        if (!expireAt) {
            return false;
        }

        const expireDate = new Date(expireAt);
        const currentDate = new Date();
        const timeDiff = expireDate - currentDate;

        return timeDiff > 0;
    });
});
const selectMemberJourneys = createSelector(selectLoyaltyJourneys, (loyalty) => {
    return loyalty?.data?.memberJourneys || [];
});
const selectCompletedJourneys = createSelector(selectLoyaltyJourneys, (loyalty) => {
    return loyalty?.data?.memberJourneys?.filter(isJourneyCompleted) || [];
});

const selectInProgressJourneys = createSelector(selectLoyaltyJourneys, (loyalty) => {
    return loyalty?.data?.memberJourneys?.filter(isJourneyInProgress) || [];
});

const selectAvailableJourneys = createSelector(selectLoyaltyJourneys, (loyalty) => {
    return loyalty?.data?.journeys || [];
});

const selectAllJourneyLevels = createSelector(selectLoyaltyJourneys, (loyalty) => {
    const journeyLevelsArray = loyalty?.data?.memberJourneys
        ?.flatMap((journey) => journey?.journeyLevels)
        ?.filter(Boolean);

    return journeyLevelsArray || [];
});

const selectStartedJourneyLevels = createSelector(selectAllJourneyLevels, (journeyLevels) => {
    const startedLevelsArray = journeyLevels
        ?.filter(isJourneyLevelInProgress)
        ?.filter((level) => level?.memberJourneyBadge);

    return startedLevelsArray;
});

const selectCompletedJourneyLevels = createSelector(selectAllJourneyLevels, (journeyLevels) => {
    const startedLevelsArray = journeyLevels?.filter(isJourneyLevelCompleted);

    return startedLevelsArray;
});

const getBadgesFromJourneyLevels = (journeyLevels) => {
    const badges = journeyLevels
        ?.filter((level) => level?.memberJourneyBadge)
        .map((level) => level?.memberJourneyBadge);

    return badges;
};

const selectAllBadges = createSelector(selectAllJourneyLevels, (journeyLevels) => {
    const badges = getBadgesFromJourneyLevels(journeyLevels);

    return badges;
});

const selectAllActions = createSelector(selectAllJourneyLevels, (journeyLevels) => {
    const actions = [];

    journeyLevels?.forEach((level) => {
        level?.memberJourneyActions?.forEach((action) => {
            actions.push({
                ...action,
                memberJourneyBadge: level.memberJourneyBadge,
                levelActions: level.memberJourneyActions,
            });
        });
    });

    return actions;
});

const selectCompletedActions = createSelector(selectAllActions, (actions) => {
    return actions.filter(isJourneyActionCompleted);
});

const selectNotCompletedActions = createSelector(selectAllActions, (actions) => {
    return actions.filter((action) => !isJourneyActionCompleted(action));
});

export {
    selectAllActions,
    selectAllBadges,
    selectAllExpiringJourneys,
    selectAllJourneyLevels,
    selectAllJourneys,
    selectAvailableJourneys,
    selectCompletedActions,
    selectCompletedJourneyLevels,
    selectCompletedJourneys,
    selectInProgressJourneys,
    selectIsLoyaltyMember,
    selectLoyaltyJourneys,
    selectLoyaltyLevel,
    selectLoyaltyPoints,
    selectMemberJourneys,
    selectNotCompletedActions,
    selectStartedJourneyLevels,
};
